import { PowerOlxV2Tab } from '../../../../components/tabs/enums/tabs.enum';
import { IParams } from '../../../../model/http';
import { CurrencyType, LOGISTICS_LOGOS } from '../../../../enum/constants';
import { OlxPricePayload, OlxPriceType } from '../../../../components/price-editor/price-editor.interface';
import {
  PlannerActionsDropdownItem,
  PlannerItem,
} from '../../../../components/layout/stacked-sidebar/components/planner-v2/planner-v2.interface';

export interface GetPowerOlxItemsParams extends IParams {
  type?: PowerOlxV2Tab;
  channel_id?: string;
  filters?: string[],
  parent_id?: string;
}

export interface OlxBalance {
  bonus: number;
  refund: number;
  sum: number;
  wallet: number;
  currency?: string;
}

export enum OlxAdvertStatus {
  ACTIVE = 'active',
  NEW = 'new',
  DISABLED = 'disabled',
  UNCONFIRMED = 'unconfirmed',
  LIMITED = 'limited',
  UNPAID = 'unpaid',
  OUTDATED = 'outdated',
  REMOVED_BY_USER = 'removed_by_user',
  MODERATED = 'moderated',
  REMOVED_BY_MODERATOR = 'removed_by_moderator',
  BLOCKED = 'blocked',
}

export const OLX_ADVERT_ACTIVE_TAB_STATUSES: OlxAdvertStatus[] = [
  OlxAdvertStatus.ACTIVE,
];

export const OLX_ADVERT_PENDING_TAB_STATUSES: OlxAdvertStatus[] = [
  OlxAdvertStatus.NEW,
  OlxAdvertStatus.DISABLED,
  OlxAdvertStatus.UNCONFIRMED,
];

export const OLX_ADVERT_UNPAID_TAB_STATUSES: OlxAdvertStatus[] = [
  OlxAdvertStatus.LIMITED,
  OlxAdvertStatus.UNPAID,
];

export const OLX_ADVERT_INACTIVE_TAB_STATUSES: OlxAdvertStatus[] = [
  OlxAdvertStatus.OUTDATED,
  OlxAdvertStatus.REMOVED_BY_USER,
];

export const OLX_ADVERT_REJECTED_TAB_STATUSES: OlxAdvertStatus[] = [
  OlxAdvertStatus.MODERATED,
  OlxAdvertStatus.REMOVED_BY_MODERATOR,
  OlxAdvertStatus.BLOCKED,
];

export interface OlxPrice {
  value?: number;
  currency?: CurrencyType;
  negotiable?: boolean;
  trade?: boolean;
  budget?: boolean;
}

export enum BulkChangePriceMode {
  IncreaseAmount = 'increase_amount',
  DecreaseAmount = 'decrease_amount',
  IncreasePercent = 'increase_percent',
  DecreasePercent = 'decrease_percent',
}

export interface BulkChangePricePayload {
  advert_ids: number[];
  mode: BulkChangePriceMode;
  value: number;
}

export interface BulkChangePriceResult {
  id: number;
  success: boolean;
  newPrice?: OlxPrice;
  error?: string;
  warning?: string;
}

export interface OlxLocation {
  city_id: number;
  city_name: string;
  district_id: number;
  district_name: string;
  region_name: string;
}

export enum OlxCategoryAttributeType {
  PRICE = 'price',
  SALARY = 'salary',
  ATTRIBUTE = 'attribute',
}

export interface OlxCategoryAttribute {
  type: OlxCategoryAttributeType;
  code: string;
  label: string;
  unit?: string;
  units?: {
    code: CurrencyType;
    label?: string;
  }[];
  validation?: {
    required?: boolean;
    numeric?: boolean;
    min?: number;
    max?: string;
    allow_multiple_values?: boolean;
  };
  values?: {
    code: OlxPriceType;
    label: string;
  }[];
}

export interface OlxLocationZone {
  id: string;
  name: string;
  sub_zones: OlxLocationZone[];
}

export interface OlxAvailablePackageFeature {
  key: string;
  label: string;
}

export interface OlxAvailablePackage {
  size: number;
  category_id: number;
  name: string;
  price: number;
  is_premium: boolean;
  type: string;
  variant_id: string;
  package_id: string;
  zone_id?: string;
  features?: OlxAvailablePackageFeature[];
}

export interface OlxCategory {
  category_id: number;
  parent_id: number;
  name: string;
  is_leaf: boolean;
  photos_limit: number;
  attributes: OlxCategoryAttribute[];
}

export interface OlxPaidCategory {
  category_id: number;
  name: string;
  icon_name: string;
}

export interface OlxPaidCategoryPackageCategory {
  id: number;
  label: string;
  categories?: OlxPaidCategoryPackageCategory[];
}

export interface OlxPaidCategoryPackage {
  id: string;
  label: string;
  type: string;
  categories: OlxPaidCategoryPackageCategory[];
  mega_packet_id?: string;
}

export interface OlxPaidCategoryPackages {
  packages: OlxPaidCategoryPackage[];
}

export enum OlxAdvertiserType {
  PRIVATE = 'private',
  BUSINESS = 'business',
}

export interface OlxAdvert {
  id: number;
  status: OlxAdvertStatus;
  url: string;
  created_at: string;
  activated_at: string;
  valid_to: string;
  title: string;
  description: string;
  category_id?: number;
  advertiser_type?: OlxAdvertiserType;
  external_id?: string;
  external_url?: string;
  contact: {
    name: string;
    phone: string;
  },
  location?: {
    city_id: number;
    district_id?: number;
    latitude?: number;
    longitude?: number;
  },
  images?: {
    url: string;
  }[];
  price?: OlxPrice;
  salary?: {
    value_from?: number;
    value_to?: number;
    currency?: string;
    negotiable?: boolean;
    type?: 'monthly' | 'hourly';
  };
  attributes?: OlxAttribute[];
  courier?: boolean;
  auto_extend_enabled?: boolean;
}

export interface OlxAdvertStatistics {
  advert_views: number;
  phone_views: number;
  users_observing: number;
  [key: string]: number;
}

export interface OlxAdvertFullStatisticsPaidFeatures {
  pushups: any[];
  topads: any[];
}

export interface OlxAdvertFullStatisticsPeriod {
  from: string;
  to: string;
}

export interface OlxAdvertFullStatisticsListItem {
  date: string;
  messages: number;
  page_views: number;
}

export interface OlxAdvertFullStatistics {
  paid_features: OlxAdvertFullStatisticsPaidFeatures;
  period: OlxAdvertFullStatisticsPeriod;
  statistics: OlxAdvertFullStatisticsListItem[];
}

export interface OlxAttribute {
  code: string;
  value?: string;
  values?: string[];
}

export enum OlxBoughtPackageType {
  BASIC = 'BASIC',
  PREMIUM = 'PREMIUM',
  MEGA = 'MEGA',
}

export interface OlxBoughtPackage {
  id: string;
  name: string;
  is_active: boolean;
  size: number;
  left: number;
  active_to: string;
  price: number;
  categories_labels: string[];
  categories_ids: number[];
  location_name: string;
  zone_id: string;
  tab_type: string;
  package_type: OlxBoughtPackageType;
}

export interface PowerOlxV2Response<T extends OlxItem> {
  items: T[];
  page?: number;
  size?: number;
  total?: number;
  total_all?: number;
  statistic?: Record<string, number>;
}

export interface BaseOlxItem extends OlxAdvert {
  is_crm: boolean;
  location?: OlxLocation;
  category?: OlxCategory;
  category_name?: string;
  contact_name?: string;
  contact_phone?: string;
  location_name?: string;
  advertiser_type_label?: string;
  codes?: string[];
  statistics?: OlxAdvertStatistics;
}

export type OlxItem = OlxAdvertItem | OlxPlannerItem | OlxBackupItem | OlxPackageItem | OlxDeliveryItem;

export interface OlxAdvertItem extends BaseOlxItem {
  tab_type: PowerOlxV2Tab.ADVERTS;
}

export interface OlxPlannerItem extends PlannerItem {
  tab_type: PowerOlxV2Tab.PLANNERS;
  is_group?: boolean;
  advert_id?: string;
  advert_title?: string;
  planner_action?: string;
  error_code?: string;
}

export interface OlxBackupItem extends Omit<BaseOlxItem, 'id' | 'created_at'> {
  tab_type: PowerOlxV2Tab.BACKUPS;
  olx_created_at: string;
  olx_id: number;
  created_at: string;
  id: string;
}

export interface OlxPackageItem extends Omit<OlxBoughtPackage, 'tab_type'> {
  tab_type: PowerOlxV2Tab.PACKAGES;
  package_tab_type: string;
}

export interface OlxDeliveryItem extends OlxDelivery {
  tab_type: PowerOlxV2Tab.DELIVERIES;
  ad_id?: number;
  ad_title?: string;
  ad_url?: string;
  ad_images?: { url: string }[];
  seller_receives?: OlxPrice;
  package_cheque_number?: string;
  package_tracking_url?: string;
  recipient_shipping_method?: string;
}

export enum OlxAdvertCommand {
  ACTIVATE = 'activate',
  DEACTIVATE = 'deactivate',
}

export interface OlxAdvertActionPayload {
  title?: string | null;
  description?: string | null;
  category_id?: number | null;
  contact?: {
    name: string | null;
    phone: string | null;
  };
  location?: {
    city_id: number | null;
    district_id: number | null;
  } | null;
  images?: { url: string }[] | null;
  price?: OlxPricePayload;
  advertiser_type?: OlxAdvertiserType | null;
  attributes?: OlxAttributePayload[];
  auto_extend_enabled?: boolean | null;
}

export interface OlxAttributePayload {
  code: string | null;
  value?: string | null;
  values?: string[] | null;
}

export enum OlxPaidFeatureCode {
  PUSH_UP = 'pushup',
}

export enum OlxAdvertStatisticName {
  PHONE_VIEWS = 'phone-views',
  ADVERT_VIEWS = 'advert-views',
}

export interface PowerOlxActionResult {
  id: number;
  success: boolean;
  error?: string;
  warning?: string;
}

export interface OlxDuplicateAdvertPayload {
  advert_ids: number[];
  replacement: boolean;
}

export enum OlxPackageStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

export enum OlxDeliveryStatusShort {
  ACCEPTED = 'accepted',
  WAITING = 'waiting',
  REJECTED = 'rejected',
}

export enum DeliveryShippingMethod {
  NOVA_POSHTA = 'NOVAPOSHTA',
  UKR_POSHTA = 'UKRPOSHTA',
  MEEST = 'MEEST',
}

export interface OlxDelivery {
  _actions: string[];
  _links: {
    conversation: {
      href: string;
    };
    packageTracking: {
      href: string;
    };
  };
  ad: {
    id: number;
    photo: string;
    title: string;
    url: string;
  };
  buyer: {
    acceptanceInfoOnReject: any;
    avatar: string;
    email: string;
    id: number;
    phone: string;
    username: string;
  };
  confirmedAt: string;
  cost: {
    cheque: any;
    commission: any[];
    delivery: {
      commission: number;
      price: number;
    };
    discount: {
      amount: number;
      name: string;
    };
    group: string;
    product: {
      commission: number;
      price: number;
    };
    promo: {
      delivery: {
        commission: number;
        price: number;
      };
      total: number;
      vendor: string;
    };
    sellerReceives: number;
    takerateFee: number;
    takerateFixedFee: number;
    takerateMaxFee: number;
    total: number;
  };
  createdAt: string;
  expiresAt: string;
  id: string;
  logistOutOfService: boolean;
  orderId: string;
  package: {
    cheque: {
      number: string;
      url: string;
    };
    statusCode: number;
    statusText: string;
  };
  paymentScheme: {
    clientId: string;
    type: string;
    version: number;
  };
  product: {
    price: number;
    quantity: number;
    takeratePercent: number;
    weight: number;
  };
  purchaseId: number;
  recipient: PersonInfo;
  rejection: any;
  seller: {
    avatar: string;
    email: string;
    id: number;
    phone: string;
    username: string;
  };
  sender: PersonInfo;
  status: string;
  statusShort: OlxDeliveryStatusShort;
}

interface PersonInfo {
  apartments: string;
  cityId: string;
  cityName: string;
  comment: string;
  email: string;
  firstName: string;
  house: string;
  lastName: string;
  officeId: string;
  officeName: string;
  patronymic: string;
  paymentMethod: string;
  phone: string;
  shippingMethod: DeliveryShippingMethod;
  street: string;
}

export interface OlxDeliveryWaybill {
  logo: string;
  name: string;
}

export const OLX_DELIVERY_DEPARTMENTS_MAP: Record<DeliveryShippingMethod, OlxDeliveryWaybill> = {
  [DeliveryShippingMethod.NOVA_POSHTA]: {
    logo: LOGISTICS_LOGOS.NOVA_POSHTA,
    name: 'nova_poshta',
  },
  [DeliveryShippingMethod.UKR_POSHTA]: {
    logo: LOGISTICS_LOGOS.UKR_POSHTA,
    name: 'ukr_poshta',
  },
  [DeliveryShippingMethod.MEEST]: {
    logo: LOGISTICS_LOGOS.MEEST,
    name: 'meest',
  },
};

export enum PLANNER_OLX_ACTION {
  CREATE_ADVERT = 'CREATE_ADVERT',
  BUY_OLX_PACKAGES = 'BUY_OLX_PACKAGES',
  BUY_OLX_ADS_BUNDLES = 'BUY_OLX_ADS_BUNDLES',
  DUPLICATE_ADVERT = 'DUPLICATE_ADVERT',
  MOVE_UP_ADVERT = 'MOVE_UP_ADVERT',
  ACTIVATE_ADVERT = 'ACTIVATE_ADVERT',
  DEACTIVATE_ADVERT = 'DEACTIVATE_ADVERT',
  CHANGE_PRICE = 'CHANGE_PRICE',
  DELETE_ADVERT = 'DELETE_ADVERT',
}

export const EDIT_OLX_CONTENT_ENABLED_ACTIONS: PLANNER_OLX_ACTION[] = [
  PLANNER_OLX_ACTION.CREATE_ADVERT,
  PLANNER_OLX_ACTION.BUY_OLX_PACKAGES,
  PLANNER_OLX_ACTION.BUY_OLX_ADS_BUNDLES,
  PLANNER_OLX_ACTION.CHANGE_PRICE,
];

export const PLANNER_OLX_ACTIONS_REGISTRY: Record<PLANNER_OLX_ACTION, PlannerActionsDropdownItem> = {
  [PLANNER_OLX_ACTION.CREATE_ADVERT]: {
    name: 'planner_action.creation',
    value: PLANNER_OLX_ACTION.CREATE_ADVERT,
  },
  [PLANNER_OLX_ACTION.BUY_OLX_PACKAGES]: {
    name: 'planner_action.package',
    value: PLANNER_OLX_ACTION.BUY_OLX_PACKAGES,
  },
  [PLANNER_OLX_ACTION.BUY_OLX_ADS_BUNDLES]: {
    name: 'planner_action.ads',
    value: PLANNER_OLX_ACTION.BUY_OLX_ADS_BUNDLES,
  },
  [PLANNER_OLX_ACTION.DUPLICATE_ADVERT]: {
    name: 'planner_action.duplication',
    value: PLANNER_OLX_ACTION.DUPLICATE_ADVERT,
    planner_config: {
      checkboxes: [{
        key: 'replacement',
        label: 'advert_delete_and_create_new',
      }],
    },
  },
  [PLANNER_OLX_ACTION.MOVE_UP_ADVERT]: {
    name: 'planner_action.push_up',
    value: PLANNER_OLX_ACTION.MOVE_UP_ADVERT,
  },
  [PLANNER_OLX_ACTION.ACTIVATE_ADVERT]: {
    name: 'planner_action.activation',
    value: PLANNER_OLX_ACTION.ACTIVATE_ADVERT,
  },
  [PLANNER_OLX_ACTION.DEACTIVATE_ADVERT]: {
    name: 'planner_action.deactivation',
    value: PLANNER_OLX_ACTION.DEACTIVATE_ADVERT,
  },
  [PLANNER_OLX_ACTION.CHANGE_PRICE]: {
    name: 'planner_action.price_change',
    value: PLANNER_OLX_ACTION.CHANGE_PRICE,
  },
  [PLANNER_OLX_ACTION.DELETE_ADVERT]: {
    name: 'planner_action.deletion',
    value: PLANNER_OLX_ACTION.DELETE_ADVERT,
    planner_config: {
      checkboxes: [{
        key: 'delete_anyway',
        label: 'planner.checkbox.delete_anyway',
      }],
    },
  },
};
